import PrepTimer from "components/Ui/PrepTimer/PrepTimer";
import { isPrepTimerRunning } from "utils/prepTimer";
import { ORDER_STATUSES } from "constants/orderStatus";
import { formatMoney } from "utils/money";
import {
    formatPickupDelta,
    isPickupOnTime,
    minutesBetween,
    formatDuration,
    formatDateTime,
} from "utils/orderTiming";

/**
 * The pieces both order detail screens are built from.
 *
 * THERE ARE TWO OF THOSE SCREENS. `/orders/:id` renders a cart and a printable
 * receipt; `/deliveryOrders/:id` is the productless variant, because three of
 * the four order entry points create orders with no cart lines at all. They
 * show the same order to the same operator and had drifted into two layouts
 * with two sets of wording — so the parts live here once and neither screen
 * spells them again.
 */

/**
 * One label and its value.
 *
 * EVERY value goes through here, because the old screens printed an empty
 * string for a missing one and left the label hanging over nothing — and on
 * this data that is normal, not rare: the address and the account phone are
 * blank on a large share of real orders. A row that shows «—» is a row an
 * operator reads past; a label over a void reads as a page that failed to load.
 */
export const Field = ({ label, children, wide = false }) => (
    <div className={`order-field${wide ? " order-field--wide" : ""}`}>
        <span className="order-field__label">{label}</span>
        <span className="order-field__value">
            {children === null || children === undefined || children === "" ? (
                <span className="order-field__empty">—</span>
            ) : (
                children
            )}
        </span>
    </div>
);

/**
 * The order's state, as a chip.
 *
 * THE RAW STATUS COLOUR IS NOT READABLE AS TEXT, which is what both screens
 * used to print. Measured against white, five of the nine fail WCAG AA — «قيد
 * التحضير» at 1.63:1 and «اكتمل» at 2.13:1. They were chosen as chip and dot
 * colours, not as ink. The stylesheet darkens the hue for the label and keeps it
 * as a wash behind, so each status still reads as itself at a glance.
 */
export const StatusPill = ({ status }) => {
    const meta = ORDER_STATUSES.find((entry) => entry.en === status);
    if (!meta) return null;

    return (
        <span className="order-status-pill" style={{ "--pill": meta.color }}>
            {meta.ar}
        </span>
    );
};

/** The pickup delta, coloured by whether the driver beat the estimate. */
export const PickupDelta = ({ seconds }) => {
    const label = formatPickupDelta(seconds);
    if (!label) return null;

    return (
        <span
            className={`order-timing ${
                isPickupOnTime(seconds) ? "order-timing--early" : "order-timing--late"
            }`}
        >
            {label}
        </span>
    );
};

const TimelineStep = ({ label, at, since }) => {
    const gap = at ? formatDuration(minutesBetween(since, at)) : null;

    return (
        <li className={`order-steps__step${at ? "" : " order-steps__step--pending"}`}>
            <span className="order-steps__mark" aria-hidden="true" />
            <span className="order-steps__label">
                {label}
                {gap ? <span className="order-steps__gap">بعد {gap}</span> : null}
            </span>
            <span className="order-steps__time">
                {formatDateTime(at) || <span className="order-steps__waiting">في الانتظار</span>}
            </span>
        </li>
    );
};

/**
 * مسار الطلب — the order's own clock, for the operator.
 *
 * The five TIMESTAMPS are the machine-readable moments the driver's score is
 * computed from. Each step carries the time elapsed since the one above it, so
 * "where did the time go" is read rather than subtracted in the reader's head.
 *
 * The customer never sees these. What the customer sees is the log below, in
 * its own card — see `CustomerTimeline`.
 */
export const OrderTimeline = ({ order }) => (
    <section className="surface-card order-card order-card--wide">
        <h4 className="order-card__title">مسار الطلب</h4>
        <div className="order-card__hint">
            أوقات الطلب كما سجّلها النظام. للإدارة فقط، ولا يراها الزبون.
        </div>
        {/* The red timer (admin#40), while the order waits for its driver. */}
        {isPrepTimerRunning(order) ? (
            <div className="order-card__timer">
                <span>الوقت المتبقي حتى يستلم السائق الطلب من المطعم</span>
                <PrepTimer order={order} fallback={null} />
            </div>
        ) : null}
        <ol className="order-steps">
            <TimelineStep label="أنشئ الطلب" at={order?.createdAt} />
            <TimelineStep
                label="بدأ التحضير"
                at={order?.prep_started_at}
                since={order?.createdAt}
            />
            <TimelineStep
                label="أُسند للسائق"
                at={order?.assigned_at}
                since={order?.prep_started_at || order?.createdAt}
            />
            <TimelineStep
                label="استلمه السائق من المطعم"
                at={order?.picked_up_at}
                since={order?.assigned_at}
            />
            <TimelineStep
                label="سُلّم للزبون"
                at={order?.delivered_at}
                since={order?.picked_up_at || order?.assigned_at}
            />
        </ol>
    </section>
);

/**
 * ما يراه الزبون — the order's log, exactly as the customer app shows it.
 *
 * Its own card because it answers a different question from «مسار الطلب»: not
 * "what happened" but "what the customer was told". The customer app renders
 * each row's `content` in the order screen, word for word, so an operator who
 * wants to know what a complaining customer saw reads it here.
 *
 * The rows are written on every status change (util/orderStatusChange.js). The
 * driver's «قبول» writes none on purpose — a customer must not learn about the
 * claim (api#145) — so it is not a missing row.
 */
export const CustomerTimeline = ({ order }) => {
    const rows = [...(order?.orderTimeLines ?? [])].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );

    return (
        <section className="surface-card order-card order-card--wide">
            <h4 className="order-card__title">ما يراه الزبون</h4>
            <div className="order-card__hint">
                هذه الرسائل تظهر للزبون في صفحة الطلب داخل التطبيق، بنفس النص.
            </div>
            {rows.length ? (
                <div className="order-log">
                    {rows.map((entry) => (
                        <div key={entry.id} className="surface-inset order-log__row">
                            <span>{entry.content}</span>
                            <span className="order-log__time">
                                {formatDateTime(entry.createdAt)}
                            </span>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="order-card__hint">لا توجد رسائل للزبون على هذا الطلب بعد.</div>
            )}
        </section>
    );
};

/** The driver's card, identical on both screens. */
export const DriverCard = ({ order }) => (
    <section className="surface-card order-card">
        <h4 className="order-card__title">السائق</h4>
        <Field label="الاسم">{order?.delivery?.user?.name}</Field>
        <Field label="الهاتف">{order?.delivery?.user?.phone}</Field>
        <Field label="الاستلام من المطعم مقابل وقت التحضير">
            <PickupDelta seconds={order?.pickup_delta_seconds} />
        </Field>
        <Field label="من الإسناد حتى التسليم">
            {formatDuration(minutesBetween(order?.assigned_at, order?.delivered_at))}
        </Field>
        <Field label="من استلام الطلب من المطعم حتى التسليم">
            {formatDuration(minutesBetween(order?.picked_up_at, order?.delivered_at))}
        </Field>
        <Field label="من إنشاء الطلب حتى التسليم">
            {formatDuration(minutesBetween(order?.createdAt, order?.delivered_at))}
        </Field>
    </section>
);

/** The four figures somebody opens an order for. */
export const OrderMoney = ({ order, createdAt }) => (
    <div className="order-stats">
        <div className="surface-card order-stat">
            <span className="order-stat__label">قيمة الطلب</span>
            <strong className="order-stat__value">{formatMoney(order?.subtotal)}</strong>
        </div>
        <div className="surface-card order-stat">
            <span className="order-stat__label">التوصيل</span>
            <strong className="order-stat__value">{formatMoney(order?.shipping)}</strong>
        </div>
        <div className="surface-card order-stat order-stat--accent">
            <span className="order-stat__label">المجموع</span>
            <strong className="order-stat__value">{formatMoney(order?.total)}</strong>
        </div>
        <div className="surface-card order-stat">
            <span className="order-stat__label">وقت الطلب</span>
            <strong className="order-stat__value order-stat__value--time">
                <bdi dir="ltr">{formatDateTime(createdAt)}</bdi>
            </strong>
        </div>
    </div>
);
